// A search result opened WITHOUT leaving the page.
//
// The loop this fixes: click a result -> navigate() -> DashboardPage
// unmounts -> smartSearchResult was component state, so it dies -> Back
// re-mounts with useState(null) and the results are gone -> search
// again. Six steps to look at two systems.
//
// Opening in a panel over the dashboard means the dashboard never
// unmounts, so the results are never lost and there is no Back to
// press. Wrong system? Close it — the results are still there, exactly
// as they were. That also makes the URL-parameter and session-cache
// work optional rather than necessary: nothing is being destroyed, so
// nothing needs restoring.

import React, { useEffect, useRef } from 'react'
import { X, ExternalLink, Check, Minus } from 'lucide-react'
import { Button } from '@/components/ui/Button'

export interface SystemPreview {
  id:                 string
  system_name?:       string | null
  system_description?: string | null
  vendor?:            string | null
  function_name?:     string | null
  cbs_grade?:         string | null
  type_approval_status?: string | null
  hull_number?:       string | null
  vessel_name?:       string | null
  has_ra?:            boolean
  has_inventory?:     boolean
  has_contacts?:      boolean
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1 text-xs">
      <span className="w-32 shrink-0 text-slate-500 dark:text-slate-400">{label}</span>
      <span className="min-w-0 flex-1 text-slate-800 dark:text-slate-100">{value}</span>
    </div>
  )
}

// present, absent — never a blank that could read as either
function Presence({ yes, label }: { yes: boolean | undefined; label: string }) {
  return (
    <span className={'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ' +
      (yes ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
           : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400')}>
      {yes ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      {label}
    </span>
  )
}

export function SystemPreviewPanel({ system, onClose, onOpenFull }: {
  system: SystemPreview | null
  onClose: () => void
  onOpenFull: (s: SystemPreview) => void
}) {
  const closeRef = useRef<HTMLButtonElement>(null)

  // Escape closes, and focus lands on the panel so a keyboard user is not
  // stranded behind the results table
  useEffect(() => {
    if (!system) return
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [system, onClose])

  if (!system) return null
  const name = system.system_name?.trim() || system.system_description?.trim() || '(unnamed system)'

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true"
         aria-label={`Preview of ${name}`}>
      {/* the dashboard stays mounted behind this — that is the whole point */}
      <div className="flex-1 bg-black/25" onClick={onClose} />
      <aside className="h-full w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white shadow-xl
                        dark:border-slate-700 dark:bg-slate-900">
        <header className="sticky top-0 flex items-start gap-2 border-b border-slate-200 bg-white px-4 py-3
                           dark:border-slate-700 dark:bg-slate-900">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-white">{name}</h2>
            <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
              {system.hull_number ?? '—'}{system.vessel_name ? ` · ${system.vessel_name}` : ''}
            </p>
          </div>
          <Button ref={closeRef} variant="ghost" size="sm" onClick={onClose}
                  className="h-7 w-7 shrink-0 p-0" title="Close (Esc)">
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="px-4 py-3">
          <div className="mb-3 flex flex-wrap gap-1.5">
            <Presence yes={system.has_ra} label="Risk assessment" />
            <Presence yes={system.has_inventory} label="Inventory" />
            <Presence yes={system.has_contacts} label="Contacts" />
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <Fact label="System"       value={system.system_name ?? '—'} />
            <Fact label="Description"  value={system.system_description ?? '—'} />
            <Fact label="Vendor"       value={system.vendor ?? '—'} />
            <Fact label="Function"     value={system.function_name ?? '—'} />
            <Fact label="CBS grade"    value={system.cbs_grade ?? '—'} />
            <Fact label="Type approval" value={system.type_approval_status ?? '—'} />
            <Fact label="Vessel"       value={system.vessel_name ?? '—'} />
            <Fact label="Hull"         value={system.hull_number ?? '—'} />
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" onClick={() => onOpenFull(system)}>
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Open full system page
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Back to results</Button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Your search results are still open behind this panel.
          </p>
        </div>
      </aside>
    </div>
  )
}
